import { fromJS } from 'immutable';
import { useState } from 'react';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Small } from 'foundations-components/transitional/components/Small';
import { TABLE } from 'reporting-data/constants/chartTypes';
import { AGGREGATION } from 'reporting-data/constants/configTypes';
import { ANALYTICS_ALL_PAGES } from 'reporting-data/constants/dataTypes';
import { IN } from 'reporting-data/constants/operators';
import { ASC, DESC } from 'reporting-data/constants/sortOrder';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import SourcesTableFilterBar from 'content-management-details-lib/components/reporting/SourcesTableFilterBar';
import { useReportingFiltersProvider } from 'content-management-details-lib/providers/ReportingFiltersProvider';
import { ALL_METRICS } from '../../constants/reporting';
import { LOSER_VARIANT, MASTER } from '../../constants/abVariations';
import { TableHoverCell } from 'foundations-components/transitional/components/Table';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ABVariationButton = ({
  hasVariationWinner,
  abVariant,
  handleChooseABWinner,
  handleRerunVariation
}) => {
  const {
    abStatus
  } = abVariant;
  if (hasVariationWinner && abStatus === MASTER || !hasVariationWinner && abStatus === LOSER_VARIANT) {
    return null;
  }
  const buttonType = hasVariationWinner ? 'rerun' : 'winner';
  return /*#__PURE__*/_jsx(Button
  // disabled={VIOLATION_STATES.includes(securityState)}
  , {
    onClick: () => {
      if (buttonType === 'winner') {
        handleChooseABWinner(abVariant.pageId);
      } else {
        handleRerunVariation(abVariant.pageId);
      }
    },
    size: "small",
    use: "tertiary-light",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `page-details-ui.testResults.abVariations.abVariationsTable.variationButton.${buttonType}`
    })
  });
};
const renderCustomRow = ({
  value: key,
  valueLabels,
  abVariantsList,
  handleChooseABWinner,
  handleRerunVariation
}) => {
  const label = valueLabels.get(0);
  const variation = abVariantsList.find(({
    id
  }) => id === key) || {
    abStatus: LOSER_VARIANT,
    id: key,
    name: label,
    pageId: Number(key),
    url: ''
  };
  const hasVariationWinner = abVariantsList.filter(({
    abStatus
  }) => abStatus !== LOSER_VARIANT).length === 1;
  return /*#__PURE__*/_jsxs(TableHoverCell, {
    hoverContent: /*#__PURE__*/_jsx(ABVariationButton, {
      abVariant: variation,
      hasVariationWinner: hasVariationWinner,
      handleChooseABWinner: handleChooseABWinner,
      handleRerunVariation: handleRerunVariation
    }),
    minInlineSize: "350px",
    children: [label, /*#__PURE__*/_jsx("br", {}), variation.abStatus && /*#__PURE__*/_jsx(Small, {
      use: "help",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `page-details-ui.testResults.abVariations.abVariationsTable.${variation.abStatus}`
      })
    })]
  }, "row-key");
};
const getReport = ({
  dateRange,
  metricsToDisplay,
  metric,
  metricSortDir,
  abVariationsIds,
  handleSortMetricColumn,
  abVariantsList,
  handleChooseABWinner,
  handleRerunVariation
}) => {
  const columns = metricsToDisplay.reduce((acc, metricToDisplay) => {
    const {
      tooltipKey
    } = ALL_METRICS[metricToDisplay];
    acc[metricToDisplay] = {
      tooltipI18nKey: tooltipKey,
      onSort: handleSortMetricColumn
    };
    return acc;
  }, {
    breakdown: {
      render: ({
        value,
        valueLabels
      }) => renderCustomRow({
        value,
        valueLabels,
        abVariantsList,
        handleChooseABWinner,
        handleRerunVariation
      })
    }
  });
  const metrics = metricsToDisplay.map(metricToDisplay => ({
    metricTypes: ALL_METRICS[metricToDisplay].metricTypes,
    property: metricToDisplay
  }));
  return fromJS({
    chartType: TABLE,
    config: {
      configType: AGGREGATION,
      dataType: ANALYTICS_ALL_PAGES,
      dimensions: ['breakdown'],
      filters: {
        custom: [{
          operator: IN,
          property: 'filters',
          values: abVariationsIds
        }],
        dateRange: {
          property: 'sessionDate',
          value: dateRange
        }
      },
      metrics,
      sort: [{
        order: metricSortDir,
        property: metric
      }]
    },
    displayParams: {
      columns,
      render: {
        hideDataLabels: true
      },
      rhumbConfig: {
        renderSuccessMarker: () => /*#__PURE__*/_jsx(NavMarker, {
          name: "AB_VARIATIONS_TABLE_LOADED"
        })
      },
      shouldTruncateHeader: false,
      showTotals: true
    }
  });
};
const ABVariationsTable = ({
  abVariationsIds,
  abVariantsList,
  handleChooseABWinner,
  handleRerunVariation
}) => {
  const [metricSortDir, setMetricSortDirection] = useState(DESC);
  const {
    metric,
    setMetric,
    metricsToDisplay,
    dateRange
  } = useReportingFiltersProvider();
  const handleSortMetricColumn = clickedMetric => {
    const {
      property
    } = fromMetricKey(clickedMetric);
    if (property === metric) {
      const newSortDir = metricSortDir === ASC ? DESC : ASC;
      setMetricSortDirection(newSortDir);
    } else {
      setMetric(property);
    }
  };
  const report = getReport({
    abVariationsIds,
    dateRange,
    metric,
    metricSortDir,
    metricsToDisplay,
    handleSortMetricColumn,
    abVariantsList,
    handleChooseABWinner,
    handleRerunVariation
  });
  if (!report) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SourcesTableFilterBar, {}), /*#__PURE__*/_jsx(CardWrapper, {
      "data-test-id": "ab-variations-table-wrapper",
      compact: true,
      children: /*#__PURE__*/_jsx(PlatformReportResolver, {
        report: report,
        useInlineLabels: false
      })
    })]
  });
};
export default ABVariationsTable;